/**
 * Knowledge Pack Library Dashboard
 * ================================
 * Dashboard-style panel layout for browsing pack categories
 * 
 * Features:
 * - 10 category panels with counters (all pack types)
 * - Minimalist EMZI CI design
 * - Click to drilldown into category
 * - Create pack modal with type selector
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRegistrySummary } from '../api/knowledgePacksV3';
import { EMZI_COLORS, EMZI_SPACING, EMZI_RADIUS, EMZI_SHADOWS } from '../styles/emziCI';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

const KnowledgePackLibraryDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState([]);
  const [overall, setOverall] = useState({ total: 0, published: 0, draft: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Pack type configurations with flat CI colors
  const packTypeConfig = {
    product: {
      icon: '📦',
      label: 'Product Packs',
      description: 'Manage product information, features, and details',
      color: EMZI_COLORS.primary
    },
    promotion: {
      icon: '🎯',
      label: 'Promotion Packs',
      description: 'Campaign promotions, offers, and special deals',
      color: EMZI_COLORS.accent
    },
    direction: {
      icon: '🎯',
      label: 'Campaign Direction',
      description: 'Strategic campaign direction and market research insights',
      color: EMZI_COLORS.primary
    },
    values: {
      icon: '💙',
      label: 'Values Packs',
      description: 'Core company values and principles',
      color: EMZI_COLORS.primary
    },
    services: {
      icon: '🧰',
      label: 'Services Packs',
      description: 'Service offerings and descriptions',
      color: '#10B981'
    },
    compliance: {
      icon: '⚖️',
      label: 'Compliance Packs',
      description: 'Compliance rules, restrictions, and guidelines',
      color: '#EF4444'
    },
    viral: {
      icon: '🚀',
      label: 'Viral Packs',
      description: 'Viral content strategies and templates',
      color: '#EC4899'
    },
    trend: {
      icon: '📈',
      label: 'Trend Packs',
      description: 'Market trends and external health context',
      color: '#06B6D4'
    },
    ambassador: {
      icon: '🌟',
      label: 'Ambassador Packs',
      description: 'Brand ambassador profiles and guidelines',
      color: '#F59E0B'
    },
    health: {
      icon: '🏥',
      label: 'Health Packs',
      description: 'Health claims, medical information, and wellness',
      color: '#8B5CF6'
    },
    format: {
      icon: '🎬',
      label: 'Format Packs',
      description: 'Content format definitions and creative intelligence rules',
      color: '#7B1FA2'
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await getRegistrySummary();
      setSummary(response.summary);
      setOverall(response.overall);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch registry summary:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePanelClick = (packType) => {
    navigate(`/knowledge-packs?type=${packType}`);
  };

  const handleCreatePack = async (packType) => {
    console.log(`[KnowledgeLibrary] 🚀 handleCreatePack called with packType: "${packType}"`);
    setShowCreateModal(false);
    
    // For product packs, create V4 pack and navigate to V4 editor
    if (packType === 'product') {
      console.log('[KnowledgeLibrary] ✅ Creating V4 product pack...');
      try {
                const newPackData = {
          pack_type: 'product',
          status: 'draft',
          identity: {
            pack_name: 'New Product Pack',
            product_name: '',
            full_description: '',
            unique_selling_points: [],
            target_customer_profile: '',
            short_description: ''
          },
          product_formulation: {
            ingredients: []
          },
          benefits: {
            product_benefits: []
          },
          social: {
            customer_testimonials: [],
            trust_badges: []
          },
          commercial: {
            pricing: []
          },
          research: {
            research_studies: []
          },
          system: {
            created_by: 'current_user',
            created_at: new Date().toISOString(),
            current_version: 1,
            version_history: []
          }
        };
        
        const response = await fetch(`${BACKEND_URL}/api/v4/product-packs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          },
          body: JSON.stringify(newPackData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to create V4 product pack');
        }
        
        const data = await response.json();
        console.log('✅ Created V4 product pack:', data);
        console.log('✅ Pack ID:', data.pack_id);
        console.log('✅ Navigating to:', `/product-packs/editor/${data.pack_id}`);
        
        // Navigate to V4 editor using window.location for reliability
        window.location.href = `/product-packs/editor/${data.pack_id}`;
      } catch (error) {
        console.error('❌ Error creating V4 product pack:', error);
        console.error('❌ Error details:', error.message, error.stack);
        // Show error in console - user can see it in F12
        alert(`Failed to create product pack: ${error.message}. Check console for details.`);
      }
    } else if (packType === 'ambassador') {
      // For ambassador packs, navigate to V4 editor directly (no creation needed)
      console.log('[KnowledgeLibrary] ✅ Navigating to Ambassador V4 create page...');
      navigate('/ambassador-packs/v4/create');
    } else {
      // For other pack types, use V3 wizard
      console.log(`[KnowledgeLibrary] ⚠️ Using V3 wizard for pack type: ${packType}`);
      navigate(`/knowledge-packs/wizard?type=${packType}`);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading pack library...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <p style={styles.errorIcon}>⚠️</p>
          <p style={styles.errorText}>{error}</p>
          <button style={styles.retryButton} onClick={fetchSummary}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>Knowledge Pack Library</h1>
          <p style={styles.subtitle}>Browse and manage knowledge packs by category - Click a pack type below to create</p>
        </div>
        {/* REMOVED: Generic "Create Pack" button - Users must click into specific pack type to create V4 packs */}
      </div>

      {/* Overall Stats - COMPACT CHIPS */}
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{overall.total}</div>
          <div style={styles.statLabel}>Total</div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, color: '#10B981'}}>{overall.published}</div>
          <div style={styles.statLabel}>Published</div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, color: '#F59E0B'}}>{overall.draft}</div>
          <div style={styles.statLabel}>Draft</div>
        </div>
      </div>

      {/* Category Panels Grid - 2-COLUMN COMPACT ≤90px */}
      <div style={styles.grid}>
        {Object.keys(packTypeConfig).map((packType) => {
          const config = packTypeConfig[packType];
          const item = summary.find(s => s.type === packType) || { 
            type: packType, 
            total: 0, 
            published: 0, 
            draft: 0 
          };
          
          return (
            <div
              key={packType}
              style={styles.panel}
            >
              <div>
                <div style={styles.panelHeader}>
                  <span style={{...styles.panelIcon, color: config.color}}>
                    {config.icon}
                  </span>
                  <h3 style={styles.panelTitle}>{config.label}</h3>
                </div>
                
                {/* Inline counters */}
                <div style={styles.panelCounters}>
                  <div style={styles.counter}>
                    <span style={styles.counterValue}>{item.total}</span>
                    <span style={styles.counterLabel}> total</span>
                  </div>
                  <span style={{ color: '#E5E7EB' }}>|</span>
                  <div style={styles.counter}>
                    <span style={styles.counterValue}>{item.total}</span>
                    <span style={styles.counterLabel}> total</span>
                  </div>
                  <span style={{ color: '#E5E7EB' }}>|</span>
                  <div style={styles.counter}>
                    <span style={{...styles.counterValue, color: '#10B981'}}>{item.published}</span>
                    <span style={styles.counterLabel}> pub</span>
                  </div>
                  <span style={{ color: '#E5E7EB' }}>|</span>
                  <div style={styles.counter}>
                    <span style={{...styles.counterValue, color: '#F59E0B'}}>{item.draft}</span>
                    <span style={styles.counterLabel}> draft</span>
                  </div>
                </div>
              </div>
              
              {/* Inline Actions */}
              <div style={styles.panelFooter}>
                <button 
                  style={styles.openButton}
                  onClick={() => handlePanelClick(packType)}
                >
                  Open
                </button>
                <button 
                  style={styles.createPackButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreatePack(packType);
                  }}
                >
                  + Create
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Pack Type Selector Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Select Pack Type</h2>
              <button
                style={styles.closeButton}
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <div style={styles.modalContent}>
              <p style={styles.modalSubtitle}>Choose the type of knowledge pack you want to create</p>
              <div style={styles.typeGrid}>
                {Object.entries(packTypeConfig).map(([type, config]) => (
                  <button
                    key={type}
                    style={styles.typeButton}
                    onClick={() => handleCreatePack(type)}
                  >
                    <span style={{...styles.typeIcon, color: config.color}}>{config.icon}</span>
                    <span style={styles.typeLabel}>{config.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles - EMZI CI Flat v6.3 (ULTRA COMPACT)
const styles = {
  container: {
    padding: EMZI_SPACING.compact,
    maxWidth: '1400px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  
  // Header (COMPACT)
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: EMZI_SPACING.md,
    flexWrap: 'wrap',
    gap: EMZI_SPACING.sm
  },
  headerContent: {
    flex: 1
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: EMZI_COLORS.textPrimary,
    margin: 0,
    marginBottom: '2px'
  },
  subtitle: {
    fontSize: '12px',
    color: EMZI_COLORS.textSecondary,
    margin: 0
  },
  createButton: {
    display: 'flex',
    alignItems: 'center',
    gap: EMZI_SPACING.xs,
    padding: '8px 16px',
    backgroundColor: EMZI_COLORS.accent,
    color: EMZI_COLORS.textWhite,
    border: 'none',
    borderRadius: EMZI_RADIUS.md,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '40px',
    transition: 'all 0.2s',
    boxShadow: EMZI_SHADOWS.sm
  },
  createIcon: {
    fontSize: '16px',
    fontWeight: '600'
  },
  
  // Stats (COMPACT CHIPS)
  statsContainer: {
    display: 'flex',
    gap: EMZI_SPACING.sm,
    marginBottom: EMZI_SPACING.md,
    flexWrap: 'wrap'
  },
  statCard: {
    backgroundColor: EMZI_COLORS.background,
    padding: `${EMZI_SPACING.sm} ${EMZI_SPACING.compact}`,
    borderRadius: EMZI_RADIUS.md,
    border: `1px solid ${EMZI_COLORS.border}`,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    gap: EMZI_SPACING.xs,
    minHeight: '40px',
    flex: '1 1 auto'
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: EMZI_COLORS.primary
  },
  statLabel: {
    fontSize: '11px',
    color: EMZI_COLORS.textSecondary,
    fontWeight: '500'
  },
  
  // Grid (2-COLUMN COMPACT)
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: EMZI_SPACING.sm,
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(2, 1fr)'
    }
  },
  
  // Panel Card (ULTRA COMPACT ≤90px)
  panel: {
    backgroundColor: EMZI_COLORS.background,
    border: `1px solid ${EMZI_COLORS.border}`,
    borderRadius: EMZI_RADIUS.md,
    padding: EMZI_SPACING.sm,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: EMZI_SHADOWS.sm,
    minHeight: '80px',
    maxHeight: '90px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: EMZI_SPACING.xs,
    marginBottom: EMZI_SPACING.xs
  },
  panelIcon: {
    fontSize: '20px',
    lineHeight: 1
  },
  panelTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1F2937',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  panelDescription: {
    display: 'none' // Hide description to save space
  },
  panelCounters: {
    display: 'flex',
    gap: EMZI_SPACING.xs,
    alignItems: 'center',
    fontSize: '11px'
  },
  counter: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px'
  },
  counterValue: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151'
  },
  counterLabel: {
    fontSize: '10px',
    color: '#9CA3AF'
  },
  panelFooter: {
    display: 'flex',
    gap: EMZI_SPACING.xs,
    marginTop: EMZI_SPACING.xs
  },
  openButton: {
    flex: 1,
    padding: '6px 8px',
    backgroundColor: 'transparent',
    border: `1px solid ${EMZI_COLORS.border}`,
    borderRadius: EMZI_RADIUS.sm,
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: EMZI_COLORS.primary
  },
  createPackButton: {
    flex: 1,
    padding: '6px 8px',
    backgroundColor: EMZI_COLORS.primary,
    border: 'none',
    borderRadius: EMZI_RADIUS.sm,
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: EMZI_COLORS.textWhite
  },
  
  // Loading
  loadingContainer: {
    textAlign: 'center',
    padding: '80px 20px'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #E5E7EB',
    borderTop: `4px solid ${EMZI_COLORS.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 16px'
  },
  loadingText: {
    color: '#6B7280',
    fontSize: '14px'
  },
  
  // Error
  errorContainer: {
    textAlign: 'center',
    padding: '80px 20px'
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  errorText: {
    color: '#EF4444',
    fontSize: '14px',
    marginBottom: '16px'
  },
  retryButton: {
    padding: '10px 24px',
    backgroundColor: EMZI_COLORS.primary,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
    pointerEvents: 'auto'
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
    pointerEvents: 'auto',
    position: 'relative',
    zIndex: 1001
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 24px 16px',
    borderBottom: '1px solid #E5E7EB'
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1F2937',
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#9CA3AF',
    cursor: 'pointer',
    lineHeight: 1,
    padding: '0 8px'
  },
  modalContent: {
    padding: '24px'
  },
  modalSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '24px'
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '12px'
  },
  typeButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '20px 16px',
    backgroundColor: 'white',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    position: 'relative',
    zIndex: 1
  },
  typeIcon: {
    fontSize: '32px'
  },
  typeLabel: {
    textAlign: 'center',
    lineHeight: '1.2'
  }
};

// Add keyframe animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media (max-width: 768px) {
    .grid {
      grid-template-columns: 1fr !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default KnowledgePackLibraryDashboard;
